// OFX parser
export function parseOFX(text) {
  const transactions = [];
  const stmtTrnRegex = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/gi;
  const tagRegex = /<(\w+)>(.*?)(?=<|\n|$)/g;

  let match;
  while ((match = stmtTrnRegex.exec(text)) !== null) {
    const block = match[1];
    const fields = {};
    let tagMatch;
    while ((tagMatch = tagRegex.exec(block)) !== null) {
      fields[tagMatch[1].toUpperCase()] = tagMatch[2].trim();
    }
    if (fields.DTPOSTED && fields.TRNAMT) {
      const raw = fields.DTPOSTED.replace(/^(\d{4})(\d{2})(\d{2}).*/, '$1-$2-$3');
      const value = Math.abs(parseFloat(fields.TRNAMT.replace(',', '.')));
      const type = parseFloat(fields.TRNAMT) >= 0 ? 'income' : 'expense';
      const desc = fields.MEMO || fields.NAME || 'Importado OFX';
      transactions.push({ description: desc, value, type, date: raw, status: 'paid', categoryId: guessCategory(desc), recurrent: false, notes: 'Importado via OFX', accountId: '' });
    }
  }
  return transactions;
}

// Normalize column name: lowercase, remove accents, remove "(R$)" etc., strip spaces
function normCol(s) {
  return (s || '').replace(/^﻿/, '').trim()
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/"/g, '')
    .replace(/\s*\(.*?\)/g, '')  // remove "(R$)", "(r$)", etc.
    .trim();
}

// Parse numeric value — handles BR (1.234,56), US (1,234.56) and simple (423,00)
function parseVal(raw) {
  if (!raw) return NaN;
  const s = raw.trim().replace(/"/g, '');
  if (!s || s === '-') return NaN;
  if (s.includes(',') && s.includes('.')) {
    return s.lastIndexOf(',') > s.lastIndexOf('.')
      ? parseFloat(s.replace(/\./g, '').replace(',', '.'))   // BR: 1.234,56
      : parseFloat(s.replace(/,/g, ''));                     // US: 1,234.56
  }
  if (s.includes(',')) return parseFloat(s.replace(',', '.')); // 423,00
  return parseFloat(s);
}

// CSV universal — detecta automaticamente o formato de qualquer banco brasileiro
export function parseCSV(text) {
  const clean = text.replace(/^﻿/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const allLines = clean.split('\n');

  // Colunas de descrição aceitas (qualquer banco)
  const descCols = [
    'historico', 'descricao', 'description', 'memo', 'lancamento', 'titulo', 'title',
    'complemento', 'estabelecimento', 'beneficiario', 'pagador', 'movimento',
    'descritivo', 'detalhes', 'details', 'observacao', 'informacao',
  ];

  // Detecta header: primeira linha com coluna de data + coluna de descrição
  let headerIdx = -1;
  let delimiter = ';';

  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    if (!line.trim()) continue;
    // Detecta delimitador: tab, ponto-e-vírgula ou vírgula
    const delim = line.includes('\t') ? '\t' : line.includes(';') ? ';' : ',';
    const cols = line.split(delim).map(normCol);
    const hasDate = cols.some(c => c === 'data' || c.startsWith('data') || c.startsWith('dt ') || c === 'dt');
    const hasDesc = cols.some(c => descCols.some(d => c === d || c.startsWith(d)));
    if (hasDate && hasDesc) {
      headerIdx = i;
      delimiter = delim;
      break;
    }
  }

  if (headerIdx < 0) return [];

  const header = allLines[headerIdx].split(delimiter).map(normCol);

  // Localiza colunas de crédito / débito (vários nomes possíveis)
  const creditKey = header.find(h =>
    h === 'credito' || h.startsWith('credito') ||
    h === 'entrada' || h.startsWith('entrada') ||
    h === 'creditos'
  );
  const debitKey = header.find(h =>
    h === 'debito' || h.startsWith('debito') ||
    h === 'saida' || h.startsWith('saida') ||
    h === 'debitos'
  );

  // Localiza coluna de valor único (com sinal)
  const valorKey = header.find(h =>
    h === 'valor' || h === 'value' || h === 'amount' ||
    h === 'montante' || h === 'quantia' || h === 'movimentacao'
  );

  // Localiza coluna de tipo (C/D ou Crédito/Débito como texto)
  const tipoKey = header.find(h => h === 'tipo' || h === 'type' || h === 'natureza' || h === 'operacao');

  // Localiza a coluna de descrição principal
  const findDesc = (row) => {
    const hist = row['historico'] || row['movimento'] || row['descritivo'] || '';
    const desc = row['descricao'] || row['description'] || row['memo'] ||
                 row['complemento'] || row['estabelecimento'] || row['beneficiario'] ||
                 row['pagador'] || row['lancamento'] || row['titulo'] || row['title'] ||
                 row['detalhes'] || row['details'] || row['observacao'] || row['informacao'] || '';
    if (hist && desc && hist.toLowerCase() !== desc.toLowerCase()) return `${hist} - ${desc}`;
    return hist || desc || 'Importado';
  };

  const transactions = [];

  for (let i = headerIdx + 1; i < allLines.length; i++) {
    const line = allLines[i];
    if (!line.trim()) continue;
    const cols = line.split(delimiter).map(c => c.trim().replace(/"/g, ''));

    const firstCol = cols[0]?.trim() || '';
    if (!firstCol) continue;
    // Linha de dado deve começar com data dd/mm/aaaa ou dd-mm-aaaa
    if (!firstCol.match(/^\d{2}[\/\-]\d{2}[\/\-]\d{4}/)) continue;

    const row = {};
    header.forEach((h, idx) => { row[h] = cols[idx] || ''; });

    const date = normalizeDate(firstCol);
    const description = findDesc(row);

    let value = 0;
    let type = 'expense';

    if (creditKey && debitKey) {
      // Colunas separadas: Crédito | Débito (Bradesco, Santander, BB, Itaú, Caixa...)
      const creditVal = parseVal(row[creditKey]);
      const debitVal  = parseVal(row[debitKey]);
      if (!isNaN(creditVal) && creditVal > 0) { value = creditVal; type = 'income'; }
      else if (!isNaN(debitVal) && debitVal !== 0) { value = Math.abs(debitVal); type = 'expense'; }
    } else if (tipoKey && valorKey) {
      // Coluna "Tipo" C/D + coluna Valor (alguns bancos cooperativos)
      const num = parseVal(row[valorKey]);
      const tipo = (row[tipoKey] || '').toLowerCase();
      if (!isNaN(num) && num !== 0) {
        value = Math.abs(num);
        type = (tipo === 'c' || tipo.startsWith('cred')) ? 'income' : 'expense';
      }
    } else {
      // Coluna única com sinal (Nubank, Inter, C6, PagBank...)
      const valRaw = (valorKey ? row[valorKey] : '') || row['valor'] || row['value'] || row['amount'] || '';
      const num = parseVal(valRaw);
      if (!isNaN(num) && num !== 0) { value = Math.abs(num); type = num >= 0 ? 'income' : 'expense'; }
    }

    if (value > 0) {
      transactions.push({ description, value, type, date, status: 'paid', categoryId: guessCategory(description), recurrent: false, notes: 'Importado via CSV', accountId: '' });
    }
  }
  return transactions;
}

function normalizeDate(raw) {
  if (!raw) return new Date().toISOString().slice(0, 10);
  // dd/mm/yyyy or dd-mm-yyyy
  const m1 = raw.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  // yyyy-mm-dd
  const m2 = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return new Date().toISOString().slice(0, 10);
}

const keywordMap = [
  { keywords: ['supermercado','mercado','extra','carrefour','pao de acucar','atacadao','cba','walmart'], cat: 'alim' },
  { keywords: ['restaurante','lanche','pizza','hamburguer','ifood','rappi','delivery','mc donald','burger'], cat: 'alim' },
  { keywords: ['combustivel','gasolina','etanol','posto','shell','ipiranga','br','petrobras'], cat: 'transp' },
  { keywords: ['uber','taxi','99','ônibus','metro','bilhete','transporte'], cat: 'transp' },
  { keywords: ['aluguel','condominio','iptu','moradia','habitacao'], cat: 'moradia' },
  { keywords: ['energia','cemig','enel','copel','light','eletrica'], cat: 'contas' },
  { keywords: ['agua','saneamento','sabesp','copasa'], cat: 'contas' },
  { keywords: ['internet','vivo','claro','tim','oi','netflix','spotify','amazon','streaming'], cat: 'contas' },
  { keywords: ['farmacia','drogaria','raia','ultrafarma','remedio','medicamento'], cat: 'saude' },
  { keywords: ['academia','smart fit','bio ritmo','plano de saude','unimed'], cat: 'saude' },
  { keywords: ['escola','faculdade','mensalidade','curso','livro','educacao'], cat: 'educ' },
  { keywords: ['salario','pagamento','prolabore','vencimento','folha'], cat: 'sal' },
  { keywords: ['freelance','servico','projeto','honorario'], cat: 'free' },
  { keywords: ['dividendo','rendimento','juros','cdb','fii','acao','investimento'], cat: 'inv' },
];

export function guessCategory(description) {
  const lower = description.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  for (const { keywords, cat } of keywordMap) {
    if (keywords.some(kw => lower.includes(kw))) return cat;
  }
  return 'other-ex';
}
